/**
 * DataTable Design Tokens
 *
 * Centralized theme system following the visual baseline from the specification.
 * All values can be overridden via the theme prop or ThemeProvider.
 */

import { DataTableTheme } from '../types';

/**
 * Default light theme tokens (matches specification)
 */
export const defaultTheme: DataTableTheme = {
  colors: {
    bg: {
      card: '#FFFFFF',
      tableHeader: '#FCFCFC',
      row: '#FFFFFF',
      rowHover: '#FAFAFA',
      rowSelected: 'rgba(230, 237, 255, 0.5)',
      bulkToolbar: 'rgba(230, 237, 255, 0.3)',
    },
    text: {
      primary: '#000000',
      secondary: '#868686',
      muted: '#666666',
      header: '#64748b', // slate-500: Better contrast than secondary for headers
    },
    border: {
      default: '#E5E7EB',
      bulkToolbar: 'rgba(58, 114, 254, 0.2)',
    },
    pill: {
      active: {
        bg: '#EEFDF7',
        text: '#666666',
      },
      error: {
        bg: '#FFF4E1',
        text: '#666666',
      },
      warning: {
        bg: '#FEF3C7',
        text: '#666666',
      },
      info: {
        bg: '#E0F2FE',
        text: '#666666',
      },
    },
    focus: {
      ring: '#094FFE',
    },
    avatar: {
      fallbackGradientFrom: '#094FFE', // mb-blue-500 equivalent
      fallbackGradientTo: '#9333EA', // purple-600 equivalent
    },
  },
  spacing: {
    cell: {
      // Match Tasks page: 20px horizontal, 16px vertical
      x: '20px',
      y: '16px',
    },
    header: {
      // Header vertical padding: 12px for emphasis and visual hierarchy
      y: '12px',
    },
    pill: {
      x: '9px',
      y: '4px',
    },
    avatar: {
      size: '44px',
    },
  },
  radius: {
    card: '14px',
    pill: '60px',
  },
  typography: {
    header: {
      size: '12px',
      weight: '500', // Medium
      lineHeight: '18px', // Tighter: 1.5 ratio for better visual density
    },
    primary: {
      size: '14px',
      weight: '600', // Semibold
      lineHeight: '20px', // Tighter: 1.43 ratio for better balance
    },
    secondary: {
      size: '12px',
      weight: '400', // Regular
      lineHeight: '18px', // Tighter: 1.5 ratio matches header
    },
    ui: {
      size: '12px',
      weight: '500', // Medium
      lineHeight: '18px', // Tighter: 1.5 ratio for compact pills
    },
  },
  heights: {
    // Header: 60px maintains visual hierarchy
    header: '60px',
    row: {
      // Comfortable: 76px provides adequate clearance for 44px avatars
      // Calculation: 76px row - (12px padding × 2) = 52px content space
      // For 44px avatar: 52px - 44px = 8px clearance (4px top, 4px bottom) ✓
      comfortable: '76px',
      compact: '50px',
    },
  },
};

/**
 * Dark theme example (theme override demonstration)
 */
export const darkTheme: DataTableTheme = {
  ...defaultTheme,
  colors: {
    ...defaultTheme.colors,
    bg: {
      card: '#1F2937',
      tableHeader: '#111827',
      row: '#1F2937',
      rowHover: '#374151',
      rowSelected: 'rgba(59, 130, 246, 0.2)',
      bulkToolbar: 'rgba(59, 130, 246, 0.15)',
    },
    text: {
      primary: '#F9FAFB',
      secondary: '#9CA3AF',
      muted: '#D1D5DB',
      header: '#94A3B8', // slate-400: Better contrast for dark mode headers
    },
    border: {
      default: '#374151',
      bulkToolbar: 'rgba(59, 130, 246, 0.3)',
    },
    pill: {
      active: {
        bg: '#064E3B',
        text: '#D1FAE5',
      },
      error: {
        bg: '#7C2D12',
        text: '#FED7AA',
      },
      warning: {
        bg: '#78350F',
        text: '#FDE68A',
      },
      info: {
        bg: '#1E3A8A',
        text: '#BFDBFE',
      },
    },
    focus: {
      ring: '#3B82F6',
    },
    avatar: {
      fallbackGradientFrom: '#3B82F6', // blue-500 for dark mode
      fallbackGradientTo: '#A855F7', // purple-500 for dark mode
    },
  },
};

/**
 * Convert theme tokens to Tailwind CSS variables
 * This enables dynamic theming at runtime
 */
export function themeToCSS(theme: DataTableTheme): Record<string, string> {
  return {
    // Colors
    '--dt-bg-card': theme.colors.bg.card,
    '--dt-bg-table-header': theme.colors.bg.tableHeader,
    '--dt-bg-row': theme.colors.bg.row,
    '--dt-bg-row-hover': theme.colors.bg.rowHover,
    '--dt-bg-row-selected': theme.colors.bg.rowSelected,
    '--dt-bg-bulk-toolbar': theme.colors.bg.bulkToolbar,

    '--dt-text-primary': theme.colors.text.primary,
    '--dt-text-secondary': theme.colors.text.secondary,
    '--dt-text-muted': theme.colors.text.muted,
    '--dt-text-header': theme.colors.text.header,

    '--dt-border-default': theme.colors.border.default,
    '--dt-border-bulk-toolbar': theme.colors.border.bulkToolbar,

    '--dt-pill-active-bg': theme.colors.pill.active.bg,
    '--dt-pill-active-text': theme.colors.pill.active.text,
    '--dt-pill-error-bg': theme.colors.pill.error.bg,
    '--dt-pill-error-text': theme.colors.pill.error.text,
    '--dt-pill-warning-bg': theme.colors.pill.warning.bg,
    '--dt-pill-warning-text': theme.colors.pill.warning.text,
    '--dt-pill-info-bg': theme.colors.pill.info.bg,
    '--dt-pill-info-text': theme.colors.pill.info.text,

    '--dt-focus-ring': theme.colors.focus.ring,

    '--dt-avatar-fallback-from': theme.colors.avatar.fallbackGradientFrom,
    '--dt-avatar-fallback-to': theme.colors.avatar.fallbackGradientTo,

    // Spacing
    '--dt-cell-x': theme.spacing.cell.x,
    '--dt-cell-y': theme.spacing.cell.y,
    '--dt-header-y': theme.spacing.header.y,
    '--dt-pill-x': theme.spacing.pill.x,
    '--dt-pill-y': theme.spacing.pill.y,
    '--dt-avatar-size': theme.spacing.avatar.size,

    // Radius
    '--dt-radius-card': theme.radius.card,
    '--dt-radius-pill': theme.radius.pill,

    // Typography
    '--dt-text-header-size': theme.typography.header.size,
    '--dt-text-header-weight': theme.typography.header.weight,
    '--dt-text-header-line-height': theme.typography.header.lineHeight,

    '--dt-text-primary-size': theme.typography.primary.size,
    '--dt-text-primary-weight': theme.typography.primary.weight,
    '--dt-text-primary-line-height': theme.typography.primary.lineHeight,

    '--dt-text-secondary-size': theme.typography.secondary.size,
    '--dt-text-secondary-weight': theme.typography.secondary.weight,
    '--dt-text-secondary-line-height': theme.typography.secondary.lineHeight,

    '--dt-text-ui-size': theme.typography.ui.size,
    '--dt-text-ui-weight': theme.typography.ui.weight,
    '--dt-text-ui-line-height': theme.typography.ui.lineHeight,

    // Heights
    '--dt-height-header': theme.heights.header,
    '--dt-height-row-comfortable': theme.heights.row.comfortable,
    '--dt-height-row-compact': theme.heights.row.compact,
  };
}

/**
 * Merge theme overrides with defaults
 */
export function mergeTheme(
  base: DataTableTheme,
  override?: Partial<DataTableTheme>
): DataTableTheme {
  if (!override) return base;

  return {
    colors: {
      bg: { ...base.colors.bg, ...override.colors?.bg },
      text: { ...base.colors.text, ...override.colors?.text },
      border: { ...base.colors.border, ...override.colors?.border },
      pill: {
        active: { ...base.colors.pill.active, ...override.colors?.pill?.active },
        error: { ...base.colors.pill.error, ...override.colors?.pill?.error },
        warning: { ...base.colors.pill.warning, ...override.colors?.pill?.warning },
        info: { ...base.colors.pill.info, ...override.colors?.pill?.info },
      },
      focus: { ...base.colors.focus, ...override.colors?.focus },
      avatar: { ...base.colors.avatar, ...override.colors?.avatar },
    },
    spacing: {
      cell: { ...base.spacing.cell, ...override.spacing?.cell },
      header: { ...base.spacing.header, ...override.spacing?.header },
      pill: { ...base.spacing.pill, ...override.spacing?.pill },
      avatar: { ...base.spacing.avatar, ...override.spacing?.avatar },
    },
    radius: { ...base.radius, ...override.radius },
    typography: {
      header: { ...base.typography.header, ...override.typography?.header },
      primary: { ...base.typography.primary, ...override.typography?.primary },
      secondary: { ...base.typography.secondary, ...override.typography?.secondary },
      ui: { ...base.typography.ui, ...override.typography?.ui },
    },
    heights: {
      header: override.heights?.header ?? base.heights.header,
      row: { ...base.heights.row, ...override.heights?.row },
    },
  };
}

/**
 * Helper to get CSS class names for theme-aware components
 *
 * IMPORTANT: Text styles REMOVED from this object to avoid Tailwind arbitrary value conflicts.
 * Use getTextStyles() function instead for typography (returns inline style objects).
 *
 * The issue: Tailwind's text-[value] utility is ambiguous - it can mean both color and size.
 * When chaining arbitrary CSS variable classes for color and size, they conflict.
 * Solution: Use inline styles for typography, Tailwind classes only for layout/spacing.
 */
export const themeClasses = {
  card: 'bg-(--dt-bg-card) rounded-(--dt-radius-card)',
  tableHeader: 'bg-[#F9FAFB] border-b border-slate-200',
  row: {
    comfortable: 'h-(--dt-height-row-comfortable) bg-(--dt-bg-row) hover:bg-(--dt-bg-row-hover)',
    compact: 'h-(--dt-height-row-compact) bg-(--dt-bg-row) hover:bg-(--dt-bg-row-hover)',
  },
  cell: 'px-4 py-4',
  headerCell: 'px-4 py-3 text-left align-middle',
  pill: {
    active: 'bg-(--dt-pill-active-bg) text-(--dt-pill-active-text)',
    error: 'bg-(--dt-pill-error-bg) text-(--dt-pill-error-text)',
    warning: 'bg-(--dt-pill-warning-bg) text-(--dt-pill-warning-text)',
    info: 'bg-(--dt-pill-info-bg) text-(--dt-pill-info-text)',
  },
  focus: 'focus:outline-none focus:ring-2 focus:ring-(--dt-focus-ring) focus:ring-offset-2',
};

/**
 * Get typography styles as inline CSS objects to avoid Tailwind conflicts
 *
 * Use this instead of themeClasses.text.* to ensure exact font sizing and styling.
 * Returns inline style objects that can be spread directly onto elements.
 *
 * @param theme - The DataTable theme object
 * @returns Object with style functions for each text variant
 *
 * @example
 * const textStyles = getTextStyles(theme);
 * <div style={textStyles.primary()}>Primary text</div>
 * <div style={textStyles.header()}>Header text</div>
 */
export function getTextStyles(theme: DataTableTheme) {
  return {
    primary: () => ({
      fontSize: theme.typography.primary.size,
      fontWeight: theme.typography.primary.weight,
      lineHeight: theme.typography.primary.lineHeight,
      color: theme.colors.text.primary,
    }),
    secondary: () => ({
      fontSize: theme.typography.secondary.size,
      fontWeight: theme.typography.secondary.weight,
      lineHeight: theme.typography.secondary.lineHeight,
      color: theme.colors.text.secondary,
    }),
    header: () => ({
      fontSize: '12px',
      fontWeight: '500',
      lineHeight: '16px', // Match Tailwind text-xs default
      color: '#64748b', // slate-500
      textTransform: 'uppercase' as const,
      letterSpacing: '0.025em', // tracking-wide
    }),
    ui: () => ({
      fontSize: theme.typography.ui.size,
      fontWeight: theme.typography.ui.weight,
      lineHeight: theme.typography.ui.lineHeight,
      color: theme.colors.text.muted,
    }),
  };
}
